import { useContext, useEffect, useMemo, useState } from "react";
import UiTable from "../../../../components/table/Table.component";
import {
	TableColumnTemplate,
	TableColumn,
} from "../../../../models/components/table/table-column.model";
import { TablePagination } from "../../../../models/components/table/table-pagination.model";
import { TableMessages } from "../../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../../models/components/field-message.model";
import { ITableHeaderRefresh } from "../../../../models/components/table/table-refresh.model";
import { EngagementHistoryRow } from "../../../../models/pages/enagement-history.model";
import { ParticipantContextProps } from "../../../../models/contexts/participant-context.model";
import UiModalEngagement from "../../../../components/modal-engagement/ModalEngagement.component";
import {
	ModalEngagementMode,
	UiModalEngagementProps,
} from "../../../../models/components/modal-engagement.model";
import { List } from "../../../../models/misc.model";
import { TableTemplateButtonsEvent } from "../../../../models/components/table/table-template.model";
import { ParticipantContext } from "../../../../contexts/Participant.context";
import {
	TableFilterType,
	ITableFilter,
	ITableHeaderFilter,
} from "../../../../models/components/table/table-filter.model";
import { useEngagementHistory } from "../../../../hooks/useEngagementHistory";
import { useCallInfo } from "../../../../hooks/useCallInfo";

const ParticipantEngagementHistory = () => {
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);

	/* Table */

	// Columns
	const columns: TableColumn[] = [
		{
			field: "outcomeTemplate",
			title: "PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TABLE.COLUMNS.outcome",
			template: TableColumnTemplate.TAG,
		},
		{
			field: "engagementDate",
			title: "PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TABLE.COLUMNS.engagementDate",
			template: TableColumnTemplate.DATE,
			templateOptions: {
				dateFormat: {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				},
			},
			sortable: true,
		},
		{
			field: "callTypeTemplate",
			title: "PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TABLE.COLUMNS.callType",
			template: TableColumnTemplate.TAG,
		},
		{
			field: "details",
			title: "PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TABLE.COLUMNS.details",
			template: TableColumnTemplate.TRANSLATE_PREFIX,
			templateOptions: { translationPrefix: "ENUMS.ENGAGEMENT_DETAILS" },
			sortable: true,
		},
		{
			field: "other",
			title: "PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TABLE.COLUMNS.other",
			sortable: true,
		},
		{
			field: "coachId",
			title: "PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TABLE.COLUMNS.createdBy",
			template: TableColumnTemplate.COACH,
			sortable: true,
		},
		{
			field: "twilioAudioTemplate",
			title: "CALL_CENTER.HISTORY.TABLE.COLUMNS.recording",
			template: TableColumnTemplate.TWILIO_AUDIO,
			style: {
				width: "100px",
			},
		},
		{
			field: "actionsTemplate",
			template: TableColumnTemplate.BUTTONS,
		},
	];

	const onPagination = async (event: TablePagination): Promise<void> => {
		const newPagination: TablePagination = {
			...pagination,
			page: event.page,
			rows: event.rows,
			sortField: event.sortField,
			sortOrder: event.sortOrder,
			first: event.first,
		};
		setPagination(newPagination);
	};

	// Refresh
	const refresh: ITableHeaderRefresh = {
		fn: () => {
			refetch();
		},
	};

	/**
	 * MODAL: ENGAGEMENT
	 */

	/**
	 * Participant information
	 * @description When receiving an incoming call, map the FROM phone number with our participant's DB.
	 * When making an outbound call, map the TO phone number with our participant's DB.
	 */

	const [selectedCallSid, setSelectedCallSid] = useState<string>("");
	const { data: participantList, isLoading: callInfoLoading } = useCallInfo(selectedCallSid);
	const [selectedRow, setSelectedRow] = useState<EngagementHistoryRow>();

	const editModal = (event: TableTemplateButtonsEvent): void => {
		const row: EngagementHistoryRow = event.row;
		const callSid = row.entity.callSid ?? "";

		// Set the callSid to trigger the hook
		setSelectedCallSid(callSid);
		setSelectedRow(row);
	};

	useEffect(() => {
		const row: EngagementHistoryRow | undefined = selectedRow;

		if (!row) {
			return undefined;
		}

		const callSid = row.entity.callSid ?? "";

		if (callSid && callInfoLoading) {
			return;
		}

		let ptList: List = [];
		if (callSid) {
			ptList = participantList || [];
		}

		setModalProps({
			...modalProps,
			edit: true,
			mode: row.entity.callSid ? ModalEngagementMode.CALL : ModalEngagementMode.NON_CALL,
			patientId: row.entity.patientId,
			participantList: ptList,
			entity: row.entity,
			isVisible: true,
		});
	}, [selectedCallSid, callInfoLoading, selectedRow]);

	const closeModalEngagement = (refresh: boolean): void => {
		setModalProps({ ...modalProps, isVisible: false });
		setSelectedRow(undefined);
		setSelectedCallSid("");

		// Refresh to get the updated data (also down the tree)
		if (refresh) {
			refetch();
		}
	};

	// Modal reminder: state
	const [modalProps, setModalProps] = useState<UiModalEngagementProps>({
		edit: false,
		mode: ModalEngagementMode.CALL,
		patientId: null,
		participantList: null,
		isVisible: false,
		closeModal: closeModalEngagement.bind(this),
	});

	/**
	 * DATA
	 */

	const participantFilter: ITableFilter = {
		field: "patientId",
		type: TableFilterType.NUMBER,
		value: [`${participantContext.participant?.id}`],
	};

	const [filters] = useState<ITableHeaderFilter | undefined>({
		filters: [participantFilter],
	});

	// Pagination
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "engagementDate",
		sortOrder: -1,
		search: undefined,
		filters: filters?.filters,
	});

	/**
	 * DATA
	 */

	const { data: engagements, isLoading, isError, refetch } = useEngagementHistory(pagination);

	// Rows
	const rows = useMemo(() => {
		if (!engagements) return [];

		return engagements.map((item) => new EngagementHistoryRow(item, editModal.bind(this)));
	}, [engagements]);

	// Messages
	const messages = new TableMessages();
	const message: UiFieldMessageProps = useMemo(() => {
		if (isLoading) return { severity: FieldMessageSeverity.LOADING, label: messages.loading };
		if (isError) return { severity: FieldMessageSeverity.DANGER, label: messages.error };
		if (!engagements || engagements.length === 0)
			return { severity: FieldMessageSeverity.INFO, label: messages.empty };
		return { severity: FieldMessageSeverity.INFO, label: "" };
	}, [isLoading, isError, engagements, messages]);

	return (
		<>
			<UiTable
				useAsCard={false}
				dataKey="id"
				title="PARTICIPANT.APPOINTMENTS.ENGAGEMENT_HISTORY.TITLE"
				columns={columns}
				value={rows}
				message={message}
				pagination={pagination}
				paginationFn={onPagination}
				refresh={refresh}
			/>

			{modalProps.isVisible && <UiModalEngagement {...modalProps}></UiModalEngagement>}
		</>
	);
};
export default ParticipantEngagementHistory;
